import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow, Polyline, OverlayView } from '@react-google-maps/api';
import { useGoogleMaps } from '../../context/GoogleMapsContext';
import TabBarIcon from '../TabBarIcon';
import { getMarkColor, getMarkType, getMarkIcon } from '../../utils/markUtils';
import { MARK_STATUS } from '../../utils/constants';
import '../../styles/UnifiedCourseMap.css';

/**
 * Boat Label Component for displaying sail numbers next to boat icons on the map.
 * 
 * This component uses Google Maps OverlayView to position text labels directly above
 * boat markers, providing immediate visual identification of boats by their sail numbers.
 * The labels are styled to be compact, readable, and non-intrusive while maintaining
 * good visibility against various map backgrounds.
 * 
 * @component
 * @example
 * // Basic usage with a boat position and sail number
 * <BoatLabel
 *   position={{ lat: 59.3293, lng: 18.0686 }}
 *   sailNumber="S2B1S1"
 *   isSelected={false}
 * />
 * 
 * @example
 * // Usage with selected state for highlighting
 * <BoatLabel
 *   position={{ lat: 59.3293, lng: 18.0686 }}
 *   sailNumber="ABC123"
 *   isSelected={true}
 * />
 * 
 * @param {Object} props - The component props
 * @param {Object} props.position - The geographical position for the label
 * @param {number} props.position.lat - Latitude coordinate (-90 to 90)
 * @param {number} props.position.lng - Longitude coordinate (-180 to 180)
 * @param {string} props.sailNumber - The sail number or boat identifier to display.
 *                                   Should be concise (typically 2-8 characters) for optimal display
 * @param {boolean} [props.isSelected=false] - Whether the boat is currently selected.
 *                                            When true, applies highlighted styling (blue background)
 * 
 * @returns {JSX.Element} A Google Maps OverlayView component containing the styled label
 * 
 * @see {@link https://developers.google.com/maps/documentation/javascript/overlayview} Google Maps OverlayView
 * 
 * @since 1.0.0
 * @version 1.0.0
 * 
 * @author Fleetly Development Team
 * 
 * @accessibility
 * - Labels use high contrast colors (white text on dark background) for readability
 * - Font size is optimized for map viewing across different zoom levels
 * - Selected state provides additional visual feedback for screen readers
 * 
 * @performance
 * - Uses CSS classes instead of inline styles for better performance
 * - Minimal DOM structure to reduce rendering overhead
 * - pointer-events: none prevents labels from interfering with boat marker clicks
 */
const BoatLabel = ({ position, sailNumber, isSelected = false }) => {
    return (
        <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
            <div className={`boat-label ${isSelected ? 'selected' : ''}`}>
                {sailNumber}
            </div>
        </OverlayView>
    );
};

// Update the MARK_DISPLAY_MODES to use IDEAL instead of PLANNED
export const MARK_DISPLAY_MODES = {
    SHOW_ACTUAL: 'showActual',
    SHOW_IDEAL: 'showIdeal',
    SHOW_BOTH: 'showBoth'
};

/**
 * Convert raw Point data from API to unified mark format for UnifiedCourseMap
 * @param {Array} rawMarks - Array of Point objects from API
 * @returns {Array} Array of unified mark objects
 */
export const convertToUnifiedMarks = (rawMarks) => {
    if (!rawMarks || !Array.isArray(rawMarks)) {
        console.warn('🗺️ convertToUnifiedMarks: Invalid input, expected array');
        return [];
    }

    const marks = [];
    
    rawMarks.forEach((point, index) => {
        // Handle first mark (mark0) - always present if latitude0/longitude0 exist
        if (point.latitude0 !== null && point.longitude0 !== null) {
            const hasActual0 = point.actual_latitude0 && point.actual_longitude0;
            
            marks.push({
                key: `${point.kind0 || point.label || point.sequence_number}_${point.latitude0}_${point.longitude0}_${point.id || index}_0`,
                label: point.kind0 || point.label || `${point.sequence_number || index + 1}`,
                name: point.kind0 || point.label || `${point.sequence_number || index + 1}`,
                lat: parseFloat(point.latitude0 || point.latitude || point.lat),
                lng: parseFloat(point.longitude0 || point.longitude || point.lng),
                actualLat: hasActual0 ? parseFloat(point.actual_latitude0) : parseFloat(point.latitude0 || point.latitude || point.lat),
                actualLng: hasActual0 ? parseFloat(point.actual_longitude0) : parseFloat(point.longitude0 || point.longitude || point.lng),
                isActualSet: hasActual0,
                status: hasActual0 ? MARK_STATUS.DROPPED : MARK_STATUS.LIFTED,
                color: getMarkColor(point.kind0 || point.label),
                type: getMarkType(point.kind0 || point.label),
                markType: point.mark_type || 'M',
                side: point.tack0 === 'S' ? 'starboard' : 'port',
                groupId: point.id || index,
                sequenceNumber: point.sequence_number || index
            });
        }
        
        // Handle second mark (mark1) - for lines and gates
        if (point.mark_type && (point.mark_type === 'L' || point.mark_type === 'G') &&
            point.latitude1 !== null && point.longitude1 !== null) {
            
            const hasActual1 = point.actual_latitude1 && point.actual_longitude1;
            
            marks.push({
                key: `${point.kind1 || point.label || point.sequence_number}_${point.latitude1}_${point.longitude1}_${point.id || index}_1`,
                label: point.kind1 || `${point.sequence_number || index + 1}B`,
                name: point.kind1 || `${point.sequence_number || index + 1}B`,
                lat: parseFloat(point.latitude1),
                lng: parseFloat(point.longitude1),
                actualLat: hasActual1 ? parseFloat(point.actual_latitude1) : parseFloat(point.latitude1),
                actualLng: hasActual1 ? parseFloat(point.actual_longitude1) : parseFloat(point.longitude1),
                isActualSet: hasActual1,
                status: hasActual1 ? MARK_STATUS.DROPPED : MARK_STATUS.LIFTED,
                color: getMarkColor(point.kind1 || point.label),
                type: getMarkType(point.kind1 || point.label),
                markType: point.mark_type,
                side: point.tack1 === 'S' ? 'starboard' : 'port',
                groupId: point.id || index,
                sequenceNumber: point.sequence_number || index
            });
        }
    });

    return marks;
};

/**
 * Simplified Course Map Component
 * Focuses only on displaying map elements - no UI controls or complex interactions
 */
const UnifiedCourseMap = ({
    // Data props
    marks = [],
    boats = [],
    userLocation = { lat: 0, lng: 0 },

    // Display configuration
    markDisplayMode = MARK_DISPLAY_MODES.SHOW_IDEAL,
    showBoats = false,
    showBoatNames = true,
    showBoatTrails = true,
    showUserLocation = false,
    showMarkLabels = true,

    // Trail configuration
    tailLengthSeconds = 60, // Default 1 minute trail

    // Mark interaction props
    allowMarkDrop = false,
    allowMarkLift = false,
    onMarkDrop = null,
    onMarkLift = null,
    selectedMark = null,

    // Basic interaction
    allowUserInteraction = true,
    autoFitBounds = true,

    // Simple event handlers
    onMarkClick = null,
    onBoatClick = null,
    onMapClick = null,
    onSelectedMarkChange = null,
    onMapReady = null, // Add this new prop

    // Map configuration
    center = null,
    zoom = 14,
    height = '100%',
    
    // Debug configuration
    debug = false
}) => {
    const { isLoaded, loadError } = useGoogleMaps();

    // Internal state - minimal
    const [map, setMap] = useState(null);
    const [internalSelectedMark, setInternalSelectedMark] = useState(null);
    const [selectedBoat, setSelectedBoat] = useState(null);
    const [mapCenter, setMapCenter] = useState(center);
    const [mapZoom, setMapZoom] = useState(zoom);
    const [infoWindow, setInfoWindow] = useState(null);

    // ADD: User interaction tracking state
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    const [initialFitDone, setInitialFitDone] = useState(false);
    const [showInfoWindow, setShowInfoWindow] = useState(false);

    // Refs
    const mapRef = useRef(null);
    const polylineInstancesRef = useRef({}); // Store polyline instances
    const prevKeysRef = useRef(new Set());

    // Boat icon helper function
    const getBoatIcon = (boat, isSelected = false, boatColor = '#ff5722') => {
        const size = isSelected ? 6 : 4;

        return {
            path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 1,
            fillColor: boatColor,
            fillOpacity: 0.9,
            scale: size,
            strokeColor: '#ffffff',
            strokeWeight: isSelected ? 3 : 2,
            rotation: boat.heading || 0
        };
    };

    // Process marks based on display mode - Updated for three-state model
    const processedMarks = useMemo(() => {

        const allMarks = [];

        marks.forEach(mark => {
            // Add color and type if not present
            const baseProcessed = {
                ...mark,
                color: mark.color || getMarkColor(mark.label)
            };

            switch (markDisplayMode) {
                case MARK_DISPLAY_MODES.SHOW_IDEAL:
                    // Show only ideal positions
                    allMarks.push({
                        ...baseProcessed,
                        displayLat: mark.lat,
                        displayLng: mark.lng,
                        isDisplayingActual: false,
                        markId: `${mark.key || mark.id}_ideal`
                    });
                    break;

                case MARK_DISPLAY_MODES.SHOW_ACTUAL:
                    // UPDATED: Show only marks that have actual coordinates AND are dropped
                    if (mark.actualLat && 
                        mark.actualLng && 
                        mark.status === 'DROPPED') {
                        
                        allMarks.push({
                            ...baseProcessed,
                            displayLat: mark.actualLat,
                            displayLng: mark.actualLng,
                            isDisplayingActual: true,
                            markId: `${mark.key || mark.id}_actual`
                        });
                    }
                    break;

                case MARK_DISPLAY_MODES.SHOW_BOTH:
                    // Show ideal position (always visible)
                    allMarks.push({
                        ...baseProcessed,
                        displayLat: mark.lat,
                        displayLng: mark.lng,
                        isDisplayingActual: false,
                        markId: `${mark.key || mark.id}_ideal`,
                        label: `${mark.label} (I)` // (I) for Ideal
                    });

                    // Show actual position only if it exists, is different, AND is dropped
                    if (mark.isActualSet && 
                        mark.actualLat && 
                        mark.actualLng &&
                        mark.status === 'DROPPED' &&
                        (mark.actualLat !== mark.lat || mark.actualLng !== mark.lng)) {
                        
                        allMarks.push({
                            ...baseProcessed,
                            displayLat: mark.actualLat,
                            displayLng: mark.actualLng,
                            isDisplayingActual: true,
                            markId: `${mark.key || mark.id}_actual`,
                            label: `${mark.label} (A)` // (A) for Actual
                        });
                    }
                    break;

                default:
                    // Default to ideal
                    allMarks.push({
                        ...baseProcessed,
                        displayLat: mark.lat,
                        displayLng: mark.lng,
                        isDisplayingActual: false,
                        markId: `${mark.key || mark.id}_ideal`
                    });
            }
        });

        return allMarks;
    }, [marks, markDisplayMode]);

    // Boat tail processing for trails
    const boatTailLines = useMemo(() => {

        // Only check trail-specific conditions, not boat visibility conditions
        if (!showBoatTrails || tailLengthSeconds === 0 || !boats) {
            return [];
        }

        const tails = [];
        // Calculate max segments based on trail duration
        // Assuming 4 points per second (update rate), this should handle variable trail lengths
        const MAX_TAIL_SEGMENTS = tailLengthSeconds === -1 ? 10000 : tailLengthSeconds * 4;
        
        // Handle array structure (your actual data format)
        if (Array.isArray(boats)) {
            
            boats.forEach((boat, index) => {
                
                // Look for trail data in different possible properties
                const rawTailData = boat.trail || boat.tail || boat.history || boat.track;
                
                if (!rawTailData || !Array.isArray(rawTailData) || rawTailData.length < 2) {
                    return;
                }

                // Convert tail data to valid Google Maps coordinates
                const validTailPoints = rawTailData
                    .filter(point => point && typeof point.lat === 'number' && typeof point.lng === 'number')
                    .filter(point => !isNaN(point.lat) && !isNaN(point.lng))
                    .filter(point => Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180)
                    .map(point => ({ lat: point.lat, lng: point.lng }));

                if (validTailPoints.length < 2) {
                    return;
                }

                const historicalTail = validTailPoints.slice(0, MAX_TAIL_SEGMENTS);
                const boatId = boat.boat_id || boat.id || index;

                // Create a stable key using boat ID and path content hash
                const pathHash = historicalTail
                    .map(p => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
                    .join('|')
                    .substring(0, 50);

                const uniqueKey = `trail-${boatId}-${pathHash}`;
                const boatColor = boat.color || '#ff5722';

                tails.push({
                    key: uniqueKey,
                    path: historicalTail,
                    options: {
                        strokeColor: boatColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 3,
                        geodesic: true,
                        zIndex: 50
                    }
                });
            });
        }
        
        // Handle object structure (legacy support)
        else if (typeof boats === 'object') {
            
            Object.entries(boats).forEach(([boatId, boatData]) => {
                
                if (!boatData || !boatData.tail) {
                    console.log(`🗺️ DEBUG: Boat ${boatId} - no tail data in object structure`);
                    return;
                }

                const rawTailData = boatData.tail;
                if (!Array.isArray(rawTailData) || rawTailData.length < 2) {
                    return;
                }

                // Convert tail data to valid Google Maps coordinates
                const validTailPoints = rawTailData
                    .filter(point => point && typeof point.lat === 'number' && typeof point.lng === 'number')
                    .filter(point => !isNaN(point.lat) && !isNaN(point.lng))
                    .filter(point => Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180)
                    .map(point => ({ lat: point.lat, lng: point.lng }));

                if (validTailPoints.length < 2) return;

                const historicalTail = validTailPoints.slice(0, MAX_TAIL_SEGMENTS);

                // Create a stable key using boat ID and path content hash
                const pathHash = historicalTail
                    .map(p => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
                    .join('|')
                    .substring(0, 50);

                const uniqueKey = `trail-${boatId}-${pathHash}`;

                // Get boat color from head data
                const boatColor = boatData.head?.color || '#ff5722';

                tails.push({
                    key: uniqueKey,
                    path: historicalTail,
                    options: {
                        strokeColor: boatColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 3,
                        geodesic: true,
                        zIndex: 50
                    }
                });
            });
        }

        return tails;
    }, [boats, showBoatTrails, tailLengthSeconds]);

    // Add polyline update function
    const updatePolylines = useCallback(() => {
        if (!map) return;

        const polylineInstances = polylineInstancesRef.current;
        const currentKeys = new Set(boatTailLines.map(tail => tail.key));
        const prevKeys = prevKeysRef.current;

        // Remove old polylines that are no longer needed
        for (const key of prevKeys) {
            if (!currentKeys.has(key) && polylineInstances[key]) {
                polylineInstances[key].setMap(null);
                delete polylineInstances[key];
            }
        }

        // Add or update polylines
        boatTailLines.forEach(tail => {
            if (polylineInstances[tail.key]) {
                // Update existing polyline
                polylineInstances[tail.key].setPath(tail.path);
                polylineInstances[tail.key].setOptions(tail.options);
            } else {
                // Create new polyline
                const polyline = new window.google.maps.Polyline({
                    path: tail.path,
                    ...tail.options,
                    map: map
                });
                polylineInstances[tail.key] = polyline;
            }
        });

        prevKeysRef.current = currentKeys;
    }, [map, boatTailLines]);

    // Add effect to update polylines 
    useEffect(() => {
        updatePolylines();
    }, [updatePolylines]);

    // Add cleanup effect for polylines
    useEffect(() => {
        return () => {
            // Cleanup all polylines when component unmounts
            const polylineInstances = polylineInstancesRef.current;
            Object.values(polylineInstances).forEach(polyline => {
                if (polyline && polyline.setMap) {
                    polyline.setMap(null);
                }
            });
            polylineInstancesRef.current = {};
            prevKeysRef.current = new Set();
        };
    }, []);

    useEffect(() => {
        if (!map) return;

        const handleUserInteraction = () => {
            setUserHasInteracted(true);
        };

        const dragEndListener = map.addListener('dragend', handleUserInteraction);
        const zoomChangedListener = map.addListener('zoom_changed', () => {
            if (initialFitDone) {
                handleUserInteraction();
            }
        });

        return () => {
            if (dragEndListener) window.google.maps.event.removeListener(dragEndListener);
            if (zoomChangedListener) window.google.maps.event.removeListener(zoomChangedListener);
        };
    }, [map, initialFitDone]);

    // 5. Auto-fit bounds effect (your existing effect with the new logic)
    useEffect(() => {
        if (!map || !autoFitBounds || processedMarks.length === 0) return;

        // Only auto-fit if user hasn't manually interacted with the map
        if (userHasInteracted) {
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();

        processedMarks.forEach(mark => {
            if (mark.displayLat && mark.displayLng) {
                bounds.extend({ lat: mark.displayLat, lng: mark.displayLng });
            }
        });

        if (showBoats) {
            if (Array.isArray(boats)) {
                boats.forEach(boat => {
                    if (boat.latitude && boat.longitude) {
                        bounds.extend({ lat: boat.latitude, lng: boat.longitude });
                    }
                });
            } else if (boats && typeof boats === 'object') {
                Object.entries(boats).forEach(([boatId, boatData]) => {
                    const boat = boatData.head || boatData;
                    if (boat && boat.lat && boat.lng) {
                        bounds.extend({ lat: boat.lat, lng: boat.lng });
                    }
                });
            }
        }

        if (showUserLocation && userLocation.lat && userLocation.lng) {
            bounds.extend(userLocation);
        }

        map.fitBounds(bounds);
        
        // Mark that initial fit is done
        if (!initialFitDone) {
            setInitialFitDone(true);
        }
    }, [map, processedMarks, boats, showBoats, userLocation, showUserLocation, autoFitBounds, userHasInteracted, initialFitDone]);
    
    const resetAutoFit = useCallback(() => {
        setUserHasInteracted(false);
        setInitialFitDone(false);
    }, []);

    // Event handlers
    const handleMapLoad = (mapInstance) => {
        setMap(mapInstance);
        mapRef.current = mapInstance;

        // PRODUCTION FIX: Force resize after a short delay to ensure proper dimensions
        setTimeout(() => {
            if (mapInstance && window.google?.maps?.event) {
                console.log('🗺️ Triggering map resize for production compatibility');
                window.google.maps.event.trigger(mapInstance, 'resize');
            }
        }, 100);

        // Additional resize after a longer delay as backup
        setTimeout(() => {
            if (mapInstance && window.google?.maps?.event) {
                console.log('🗺️ Triggering backup map resize');
                window.google.maps.event.trigger(mapInstance, 'resize');
            }
        }, 500);

        // Call onMapReady if provided, passing map controls
        if (onMapReady) {
            onMapReady({ map: mapInstance, resetAutoFit });
        }
    };

    // Update the handleMarkClick function (around line 375):
    const handleMarkClick = useCallback((mark) => {
        // Validate mark data before proceeding
        if (!mark || !mark.lat || !mark.lng || 
            isNaN(mark.lat) || isNaN(mark.lng)) {
            console.error('🗺️ ERROR: Invalid mark data:', mark);
            return;
        }
        
        // Prevent any potential state conflicts by clearing first
        setInfoWindow(null);
        setShowInfoWindow(false);
        
        // Update selected mark state
        setInternalSelectedMark(mark);
        
        // Use the mark's current display coordinates
        const displayLat = mark.isDisplayingActual && mark.actualLat ? mark.actualLat : mark.lat;
        const displayLng = mark.isDisplayingActual && mark.actualLng ? mark.actualLng : mark.lng;
        
        // Validate display coordinates
        if (isNaN(displayLat) || isNaN(displayLng) || 
            Math.abs(displayLat) > 90 || Math.abs(displayLng) > 180) {
            console.error('🗺️ ERROR: Invalid display coordinates:', { displayLat, displayLng });
            return;
        }
        
        const position = { 
            lat: Number(displayLat), 
            lng: Number(displayLng) 
        };
        
        // Create info window content
        const infoContent = (
            <div className="mark-info">
                <h4>{mark.label || mark.name || 'Mark'}</h4>
                <div className="mark-details">
                    <p><strong>Type:</strong> {mark.isDisplayingActual ? 'Actual Position' : 'Ideal Position'}</p>
                    <p><strong>Position:</strong> {position.lat.toFixed(6)}, {position.lng.toFixed(6)}</p>
                    {mark.isActualSet && mark.actualLat && mark.actualLng && (
                        <p><strong>Actual:</strong> {mark.actualLat.toFixed(6)}, {mark.actualLng.toFixed(6)}</p>
                    )}
                    <p><strong>Status:</strong> {mark.status || 'Unknown'}</p>
                </div>
            </div>
        );
        
        // Set info window with delay to prevent conflicts
        setTimeout(() => {
            setInfoWindow({
                position: position,
                content: infoContent
            });
            setShowInfoWindow(true);
        }, 50);

        // Call external handlers
        if (onMarkClick) {
            onMarkClick(mark);
        }
        
        if (onSelectedMarkChange) {
            onSelectedMarkChange(mark);
        }
    }, [onMarkClick, onSelectedMarkChange]);

    // Update renderMarkers function to fix event handling:
    const renderMarkers = useCallback(() => {
        if (!processedMarks || processedMarks.length === 0) {
            return null;
        }

        return processedMarks.map(mark => {
            if (!mark.displayLat || !mark.displayLng || 
                isNaN(mark.displayLat) || isNaN(mark.displayLng)) {
                console.warn('🗺️ WARNING: Skipping invalid mark:', mark);
                return null;
            }

            // Fix the selection logic
            const currentSelected = selectedMark || internalSelectedMark;
            const isSelected = currentSelected && (
                currentSelected.key === mark.key || 
                currentSelected.label === mark.label ||
                (currentSelected.lat === mark.lat && currentSelected.lng === mark.lng)
            );

            return (
                <Marker
                    key={`marker-${mark.key || mark.label}-${mark.displayLat}-${mark.displayLng}`}
                    position={{ 
                        lat: Number(mark.displayLat), 
                        lng: Number(mark.displayLng) 
                    }}
                    icon={getMarkIcon(mark, isSelected, mark.isDisplayingActual)}
                    title={mark.label || mark.name || 'Mark'}
                    onClick={(e) => {
                        // Critical: Stop event propagation
                        if (e && e.domEvent) {
                            e.domEvent.stopPropagation();
                            e.domEvent.preventDefault();
                        }
                        
                        // Stop the event from bubbling to the map
                        if (e && e.stop) {
                            e.stop();
                        }
                        
                        // Handle click with delay to ensure event processing completes
                        setTimeout(() => {
                            handleMarkClick(mark);
                        }, 10);
                    }}
                    zIndex={isSelected ? 1000 : (mark.isDisplayingActual ? 150 : 100)}
                />
            );
        }).filter(marker => marker !== null);
    }, [processedMarks, selectedMark, internalSelectedMark, debug, handleMarkClick]);

    // Update renderInfoWindow with better error handling:
    const renderInfoWindow = useCallback(() => {
        if (!showInfoWindow || !infoWindow) {
            return null;
        }
        
        // Validate info window position
        if (!infoWindow.position || 
            typeof infoWindow.position.lat !== 'number' || 
            typeof infoWindow.position.lng !== 'number' ||
            isNaN(infoWindow.position.lat) || 
            isNaN(infoWindow.position.lng) ||
            Math.abs(infoWindow.position.lat) > 90 ||
            Math.abs(infoWindow.position.lng) > 180) {
            console.error('🗺️ ERROR: Invalid InfoWindow position:', infoWindow.position);
            
            // Clear invalid info window
            setInfoWindow(null);
            setShowInfoWindow(false);
            return null;
        }

        return (
            <InfoWindow
                position={{
                    lat: Number(infoWindow.position.lat),
                    lng: Number(infoWindow.position.lng)
                }}
                onCloseClick={() => {
                    setInfoWindow(null);
                    setShowInfoWindow(false);
                    setInternalSelectedMark(null);
                    if (onSelectedMarkChange) {
                        onSelectedMarkChange(null);
                    }
                }}
                options={{
                    pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -30) : undefined,
                    maxWidth: 300,
                    disableAutoPan: false,
                    closeBoxURL: "", // Use default close button
                    enableEventPropagation: false // Prevent event bubbling
                }}
            >
                <div className="info-window-content">
                    {infoWindow.content || <div>No content available</div>}
                </div>
            </InfoWindow>
        );
    }, [showInfoWindow, infoWindow, onSelectedMarkChange]);

    const renderUserLocation = useCallback(() => {
        if (!showUserLocation || !userLocation.lat || !userLocation.lng) return null;

        return (
            <Marker
                position={userLocation}
                icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                    fillColor: '#4285f4',
                    fillOpacity: 1,
                    scale: 8,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                }}
                title="Your Location"
                zIndex={500}
            />
        );
    }, [showUserLocation, userLocation]);

    // Update handleMapClick to be more defensive:
    const handleMapClick = useCallback((event) => {
        // Only clear selections if we're sure this is a map click, not a marker click
        setTimeout(() => {
            // Clear selection states
            setInternalSelectedMark(null);
            setSelectedBoat(null);
            
            // Clear info window
            setInfoWindow(null);
            setShowInfoWindow(false);

            // Call external handlers
            if (onSelectedMarkChange) {
                onSelectedMarkChange(null);
            }
            if (onMapClick) {
                onMapClick(event);
            }
        }, 100); // Small delay to allow marker clicks to process first
    }, [onSelectedMarkChange, onMapClick]);

    // Add a map refresh function for emergency recovery:
    const refreshMap = useCallback(() => {
        if (map) {
            // Clear all overlays
            setInfoWindow(null);
            setShowInfoWindow(false);
            setInternalSelectedMark(null);
            setSelectedBoat(null);
            
            // Force map to re-render by triggering a resize
            setTimeout(() => {
                if (window.google && window.google.maps) {
                    window.google.maps.event.trigger(map, 'resize');
                }
            }, 100);
        }
    }, [map]);

    // Update the renderBoats function to handle both data structures (replace existing renderBoats around line 650)
    const renderBoats = useCallback(() => {
        if (!showBoats) {
            return null;
        }
        
        if (!boats) {
            return null;
        }

        const boatMarkers = [];

        // Handle object structure (your current API format)
        if (typeof boats === 'object' && !Array.isArray(boats)) {
            Object.entries(boats).forEach(([boatId, boatData]) => {
                const boat = boatData.head || boatData;
                
                if (!boat || !boat.lat || !boat.lng || 
                    isNaN(boat.lat) || isNaN(boat.lng)) {
                    console.warn('🗺️ WARNING: Invalid boat head data for boat', boatId, ':', boat);
                    return;
                }

                const isSelected = selectedBoat && (
                    selectedBoat.boat_id === boatId || 
                    selectedBoat.id === boatId
                );

                const sailNumber = boat.sail_number || boatId;
                const position = { 
                    lat: Number(boat.lat), 
                    lng: Number(boat.lng) 
                };

                // Add the boat marker
                boatMarkers.push(
                    <Marker
                        key={`boat-${boatId}`}
                        position={position}
                        icon={getBoatIcon(boat, isSelected, boat.color)}
                        title={`Boat ${boat.label || boatId}`}
                        onClick={() => {
                            // Handle boat info window (similar to marker click)
                            
                            // Create simplified boat info window content - only show essential info
                            const boatName = boat.label || boat.boat_name || `Boat ${boatId}`;
                            const boatId_display = boat.sail_number || boatId;
                            const boatClass = boat.boat_class || 'Unknown Class';
                            const owner = boat.owner || boat.boat_owner || 'Unknown Owner';
                            const activeSails = boat.sail_number || boat.active_sail || 'No active sail';
                            
                            const infoContent = (
                                <div className="boat-info">
                                    <h4>⛵ {boatName}</h4>
                                    <div className="boat-details">
                                        <p><strong>Boat ID:</strong> {boatId_display}</p>
                                        <p><strong>Class:</strong> {boatClass}</p>
                                        <p><strong>Owner:</strong> {owner}</p>
                                        <p><strong>Active Sails:</strong> {activeSails}</p>
                                    </div>
                                </div>
                            );
                            
                            // Set info window with delay to prevent conflicts  
                            setTimeout(() => {
                                setInfoWindow({
                                    position: position,
                                    content: infoContent
                                });
                                setShowInfoWindow(true);
                            }, 50);
                            
                            setSelectedBoat({ ...boat, boat_id: boatId });
                            if (onBoatClick) {
                                onBoatClick({ ...boat, boat_id: boatId });
                            }
                        }}
                        zIndex={isSelected ? 2000 : 200}
                    />
                );

                // Add the sail number label positioned above the boat marker
                // This provides immediate visual identification without requiring user interaction
                if (showBoatNames) {
                    boatMarkers.push(
                        <BoatLabel
                            key={`boat-label-${boatId}`}
                            position={position}
                            sailNumber={sailNumber}
                            isSelected={isSelected}
                        />
                    );
                }
            });
        }
        
        // Handle array structure (legacy support)
        else if (Array.isArray(boats)) {
            boats.forEach((boat, index) => {
                if (!boat || (!boat.latitude && !boat.lat) || (!boat.longitude && !boat.lng)) {
                    console.warn('🗺️ WARNING: Invalid boat array data:', boat);
                    return;
                }

                const lat = boat.latitude || boat.lat;
                const lng = boat.longitude || boat.lng;

                const isSelected = selectedBoat && (
                    selectedBoat.boat_id === boat.boat_id || 
                    selectedBoat.id === boat.id
                );

                const sailNumber = boat.sail_number || boat.boat_id || `${index + 1}`;
                const position = { lat: Number(lat), lng: Number(lng) };

                // Add the boat marker
                boatMarkers.push(
                    <Marker
                        key={`boat-array-${boat.boat_id || index}`}
                        position={position}
                        icon={getBoatIcon(boat, isSelected, boat.color)}
                        title={`Boat ${boat.label || boat.boat_id || index}`}
                        onClick={() => {
                            // Handle boat info window (similar to marker click)
                            
                            // Create simplified boat info window content for array format - only show essential info
                            const boatName = boat.label || boat.boat_name || `Boat ${boat.boat_id || index}`;
                            const boatId_display = boat.sail_number || boat.boat_id || `${index + 1}`;
                            const boatClass = boat.boat_class || 'Unknown Class';
                            const owner = boat.owner || boat.boat_owner || 'Unknown Owner';
                            const activeSails = boat.sail_number || boat.active_sail || 'No active sail';
                            
                            const infoContent = (
                                <div className="boat-info">
                                    <h4>⛵ {boatName}</h4>
                                    <div className="boat-details">
                                        <p><strong>Boat ID:</strong> {boatId_display}</p>
                                        <p><strong>Class:</strong> {boatClass}</p>
                                        <p><strong>Owner:</strong> {owner}</p>
                                        <p><strong>Active Sails:</strong> {activeSails}</p>
                                    </div>
                                </div>
                            );
                            
                            // Set info window with delay to prevent conflicts
                            setTimeout(() => {
                                setInfoWindow({
                                    position: position,
                                    content: infoContent
                                });
                                setShowInfoWindow(true);
                            }, 50);
                            
                            setSelectedBoat(boat);
                            if (onBoatClick) {
                                onBoatClick(boat);
                            }
                        }}
                        zIndex={isSelected ? 2000 : 200}
                    />
                );

                // Add the sail number label positioned above the boat marker
                // This provides immediate visual identification without requiring user interaction
                if (showBoatNames) {
                    boatMarkers.push(
                        <BoatLabel
                            key={`boat-array-label-${boat.boat_id || index}`}
                            position={position}
                            sailNumber={sailNumber}
                            isSelected={isSelected}
                        />
                    );
                }
            });
        }
        return boatMarkers;
    }, [showBoats, boats, selectedBoat, onBoatClick, getBoatIcon, showBoatNames]);

    // Update the main GoogleMap component render:
    return (
        <div className="unified-course-map">
            <GoogleMap
                mapContainerStyle={{
                    width: '100%',
                    height: height,
                    minHeight: '400px', // Fallback minimum height
                    position: 'relative',
                    display: 'block' // Ensure proper layout
                }}
                center={center}
                zoom={zoom}
                onLoad={handleMapLoad}
                onClick={handleMapClick}
                options={{
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: true,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    gestureHandling: allowUserInteraction ? 'greedy' : 'none',
                    disableDoubleClickZoom: false,
                    clickableIcons: false
                }}
            >
                {/* Render markers */}
                {renderMarkers()}
                                
                {/* Render boats */}
                {renderBoats()}
                
                {/* Render user location */}
                {renderUserLocation()}
                
                {/* Render info window */}
                {renderInfoWindow()}
            </GoogleMap>
        </div>
    );
};

export default UnifiedCourseMap;